<template>
  <div class="container">
    <!-- Add Education Button -->
    <v-card elevation="10">
      <v-banner color="blue darken-3" outline class="add-education-btn1">
        Add Education History
      </v-banner>
    </v-card>

    <!-- Row for Button Toggle -->
    <v-row class="mb-5 pb-10 justify-center">
      <v-col cols="12">
        <div v-if="$vuetify.display.mdAndUp">
          <v-btn-toggle
            v-model="text"
            :class="buttonToggleClass"
            group
            rounded="0"
            class="mb-5 mt-5"
          >
            <v-btn
              value="High School"
              :class="buttonClass"
              color="blue darken-3"
              @click="openDialog('High School')"
            >
              <v-icon :class="iconClass">mdi-plus</v-icon>High School
            </v-btn>
            <v-btn
              value="Diploma"
              :class="buttonClass"
              color="blue darken-3"
              @click="openDialog('Diploma')"
            >
              <v-icon :class="iconClass">mdi-plus</v-icon>Diploma
            </v-btn>
            <v-btn
              value="BA/BSC"
              :class="buttonClass"
              color="blue darken-3"
              @click="openDialog('BA/BSC')"
            >
              <v-icon :class="iconClass">mdi-plus</v-icon>Bachelor's Degree
            </v-btn>
            <v-btn
              value="MA/MSC"
              :class="buttonClass"
              color="blue darken-3"
              @click="openDialog('MA/MSC')"
            >
              <v-icon :class="iconClass">mdi-plus</v-icon>Master's Degree
            </v-btn>
            <v-btn
              value="PhD"
              :class="buttonClass"
              color="blue darken-3"
              @click="openDialog('PhD')"
            >
              <v-icon :class="iconClass">mdi-plus</v-icon>PhD
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- Dropdown for Mobile View -->
        <div v-else>
          <v-select
            v-model="text"
            :items="educationLevels"
            label="Select Education Level"
            @change="openDialog(text)"
            dense
            outlined
            class="mb-5 mt-5"
          ></v-select>
          <v-btn class="add-education-btn" @click="handleAddEducationClick">
            <v-icon>mdi-plus</v-icon>Add {{ text || 'Education' }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Education Information Table -->
    <v-data-table :headers="headers" :items="education" item-key="index" class="table">
      <template v-slot:[`item.level`]="{ item, index }">
        <td :class="editableClass(item.level)">
          <span v-if="!item.isEditing">{{ item.level }}</span>
          <v-text-field v-else v-model="item.level" dense hide-details @blur="stopEditing('level', index)" />
        </td>
      </template>
      <template v-slot:[`item.fieldOfStudy`]="{ item, index }">
        <td @click="editCell('fieldOfStudy', index)" :class="editableClass(item.fieldOfStudy)">
          <span v-if="!item.isEditing">{{ item.fieldOfStudy }}</span>
          <v-text-field v-else v-model="item.fieldOfStudy" dense hide-details @blur="stopEditing('fieldOfStudy', index)" />
        </td>
      </template>
      <template v-slot:[`item.institution`]="{ item, index }">
        <td @click="editCell('institution', index)" :class="editableClass(item.institution)">
          <span v-if="!item.isEditing">{{ item.institution }}</span>
          <v-text-field v-else v-model="item.institution" dense hide-details @blur="stopEditing('institution', index)" />
        </td>
      </template>
      <template v-slot:[`item.entryYear`]="{ item, index }">
        <td @click="editCell('entryYear', index)" :class="editableClass(item.entryYear)">
          <span v-if="!item.isEditing">{{ item.entryYear }}</span>
          <v-text-field v-else v-model="item.entryYear" dense hide-details type="number" @blur="stopEditing('entryYear', index)" />
        </td>
      </template>
      <template v-slot:[`item.graduationYear`]="{ item, index }">
        <td @click="editCell('graduationYear', index)" :class="editableClass(item.graduationYear)">
          <span v-if="!item.isEditing">{{ item.graduationYear }}</span>
          <v-text-field v-else v-model="item.graduationYear" dense hide-details type="number" @blur="stopEditing('graduationYear', index)" />
        </td>
      </template>
      <template v-slot:[`item.actions`]="{ index }">
        <td>
          <v-btn icon size="x-small" @click="openDialog(null, index)" color="blue" class="edit-btn">
            <v-icon size="x-small">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon size="x-small" @click="deleteEducation(index)" color="red" class="delete-btn">
            <v-icon size="x-small">mdi-delete</v-icon>
          </v-btn>

        </td>
      </template>
    </v-data-table>

    <!-- Education Form Dialog -->
    <v-dialog v-model="dialog" max-width="900px" :eager="true">
      <v-card class="card elevation-20 stylish-card">
        <v-card-title>
          <span class="header-title">{{ editMode ? 'Edit' : 'Add' }} <span style="color: #01739F;">{{newEducation.level}}</span>  Education Information</span>
        </v-card-title>
        <v-card-text class="container">
          <v-form ref="form" v-model="isValid" class="form">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Education Level"
                  v-model="newEducation.level"
                  :rules="[rules.required]"
                  variant="outlined"
                  dense
                  disabled
                  color="blue"
                  hide-details="auto"
                  style="color:#01739F;font-size: 1.2rem; font-weight: bold; font-family: 'Roboto', sans-serif; "
                >
                  <template v-slot:prepend>
                    <v-icon color="red">mdi-chevron-triple-up</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-if="newEducation.level ==='High School'"
                  label="Entry Grade *"
                  v-model="newEducation.entryGrade"
                  default-value=9
                  :items="entryGrades"
                  variant="outlined"
                  dense
                  class="input-field"
                  color="blue"
                  hide-details="auto"
                >
                  <template v-slot:prepend>
                    <v-icon color="red">mdi-school</v-icon>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Field of Study/Specialization *"
                  v-model="newEducation.fieldOfStudy"
                  :rules="[rules.required]"
                  variant="outlined"
                  dense
                  class="input-field"
                  color="blue"
                  hide-details="auto"
                >
                  <template v-slot:prepend>
                    <v-icon color="red">mdi-book-open</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Institution *"
                  v-model="newEducation.institution"
                  :rules="[rules.required]"
                  variant="outlined"
                  dense
                  class="input-field"
                  color="blue"
                  hide-details="auto"
                >
                  <template v-slot:prepend>
                    <v-icon color="red">mdi-school</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Entry Year (E.C) *"
                  type="number"
                  v-model="newEducation.startYear"
                  :rules="[rules.required, rules.numeric, rules.validYear]"
                  variant="outlined"
                  dense
                  class="input-field"
                  color="blue"
                  hide-details="auto"
                >
                  <template v-slot:prepend>
                    <v-icon color="red">mdi-calendar-check</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Graduation/Exit Year (E.C) "
                  type="number"
                  v-model="newEducation.endYear"
                  :rules="[rules.required, rules.numeric, rules.validYear]"
                  variant="outlined"
                  dense
                  class="input-field"
                  color="blue"
                  hide-details="auto"
                >
                  <template v-slot:prepend>
                    <v-icon color="red">mdi-calendar-check</v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="closeDialog" class="add-education-btn">Cancel</v-btn>
          <v-btn color="white darken-3" @click="submitEducation" class="add-education-btn">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for validation messages -->
    <v-snackbar v-model="snackbar" :timeout="3000" color="red" top>
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <!-- Next Button -->
    <!-- <v-btn color="primary" @click="validateAndProceed">Next</v-btn> -->
  </div>
</template>

<script>
import { reactive } from 'vue';

export default {
  props: {
    // Receive the education data from the parent component
    education: {
      type: Array,
      required: true,
    },
  },
  name: "EducationForm",
  data() {
    return {
      dialog: false,
      isValid: false,
      snackbar: false,
      snackbarMessage: '',
      text: 'center',
      icon: 'justify',
      toggle_none: null,
      toggle_one: 0,
      toggle_exclusive: 2,
      toggle_multiple: [0, 1, 2, 3, 4],
      text: '', // Model for v-btn-toggle and v-select

      educationLevels: ['High School', 'Diploma', 'BA/BSC', 'MA/MSC', 'PhD'], // Education levels for dropdown
      entryGrades: [9,10,11,12],

      education: reactive([]), // Use reactive to make education an observable array
      newEducation: {
        level: "",
        fieldOfStudy: "",
        institution: "",
        startYear: "",
        endYear: "",
      },
      editMode: false, // Add editMode to track if we are in edit mode
      editIndex: null, // Add editIndex to track which entry is being edited
      rules: {
        required: value => !!value || "This field is required.",
        numeric: value => !isNaN(value) || "Must be a number.",
        validYear: value => value >= 1980 && value <= 2030 || "Please enter a valid year.",
        graduationYear: value => value >= this.newEducation.startYear || "Graduation year must be greater than or equal to entry year.",
      },
      headers: [
        { title: 'Education Level', align: 'start', key: 'level' },
        { title: 'Field of Study', align: 'start', key: 'fieldOfStudy' },
        { title: 'Institution', align: 'start', key: 'institution' },
        { title: 'Entry Grade', align: 'start', key: 'entryGrade' },
        { title: 'Entry Year (E.C)', align: 'start', key: 'startYear' },
        { title: 'Graduation Year (E.C)', align: 'start', key: 'endYear' },
        { title: 'Actions', align: 'center', key: 'actions' },
      ],
    };
  },
  watch: {
    // Watch the education data for changes and emit updates to the parent
    education: {
      handler(newData) {
        this.$emit('update-form-data', newData);
      },
      deep: true,
    },
    dialog(value) {
      if (!value) {
        this.resetForm();
      }
    },
  },

  computed: {
    userEducation() {
      return this.$store.getters.user.educations;
    },
    buttonClass() {
      return this.$vuetify.display.mdAndUp ? 'btn-small' : 'btn-small';
    },
    buttonToggleClass() {
      return this.$vuetify.display.mdAndUp ? 'btn-toggle-small' : 'btn-toggle-small';
    },
    iconClass() {
      return this.$vuetify.display.mdAndUp ? 'icon-small' : 'icon-small';
    },
  },

  mounted() {
    if (Array.isArray(this.userEducation) && this.userEducation.length > 0) {
      this.education = [...this.userEducation]; // Ensure it is an array
      this.newEducation = { ...this.userEducation[0] };
    } else {
      this.education = [];
    }
    console.log("Existing Education:", this.education);
  },
  methods: {
    handleAddEducationClick() {
      if (!this.text) {
        this.snackbarMessage = "Please Select Education Level first!!";
        this.snackbar = true;
      } else {
        this.openDialog(this.text);
      }
    },
    openDialog(level = null, index = null) {
      if (index !== null) {
        // Edit mode
        this.editMode = true;
        this.editIndex = index;
        this.newEducation = { ...this.education[index] };
      } else {
        // Add mode
        this.editMode = false;
        this.newEducation = {
          level: level || "",
          entryGrade: "",
          fieldOfStudy: "",
          institution: "",
          startYear: "",
          endYear: "",
        };
      }
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.newEducation = {
        level: "",
        fieldOfStudy: "",
        institution: "",
        startYear: "",
        endYear: "",
      };
      this.isValid = false;
      this.snackbar = false;
      this.snackbarMessage = '';
    },
    editCell(field, index) {
      this.education[index].isEditing = true;
    },
    stopEditing(field, index) {
      this.education[index].isEditing = false;
    },
    hasHighSchoolInfo() {
      return this.education.some(edu => edu.level === 'High School');
    },
    submitEducation() {
      if (
        this.newEducation.level &&
        this.newEducation.fieldOfStudy &&
        this.newEducation.institution &&
        this.newEducation.startYear &&
        this.newEducation.endYear
      ) {
        if (this.editMode) {
          // Update existing entry
          this.education[this.editIndex] = { ...this.newEducation };
        } else {
          // Add new entry
          this.education.push({ ...this.newEducation });
        }
        this.dialog = false;
        this.resetForm();
      } else {
        this.snackbarMessage = "Please fill in all required fields.";
        this.snackbar = true;
      }
    },
    deleteEducation(index) {
      this.education.splice(index, 1);
    },
    async validateForm() {
      const isValid = await this.$refs.form.validate();
      console.log('Form validation:', isValid);
      this.formIsValid = isValid.valid;
      console.log(isValid.valid ? 'All fields are valid.' : 'Validation failed.');
      if (!this.hasHighSchoolInfo())
        return false;
      else
        return true;
      return isValid.valid;
      // return true;
    },
    editableClass(value) {
      return value ? "editable-cell" : "";
    },
  },
};
</script>




<style scoped>


.container {
  max-width: 1000px;
  margin: 10px auto;
  padding: 10px;
  background: linear-gradient(135deg, #f0f4f8, #d9e2ec);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border: 1px solid #e1e7ef;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  position: relative;
}

.container::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
  animation: rotate-bg 8s linear infinite;
  z-index: 0;
}

.container:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

.container > * {
  position: relative;
  z-index: 1;
}

@keyframes rotate-bg {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}


.delete-btn {
  color: red;
}

/* Container */


.add-education-btn {
  margin-top: 20px;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 14px 10px;
  margin-left:10px;
  border-radius: 12px;
  background: linear-gradient(135deg, #ea2f00, #410101);
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2), 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s, box-shadow 0.3s, transform 0.2s ease-in-out;
  border: 2px solid transparent;
  cursor: pointer;
  
  /* Center the text */
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}
.add-education-btn1 {
  margin-top: 10px;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 4px 40px;
  border-radius: 2px;
  background: linear-gradient(135deg, #0089b3, #043766);
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2), 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s, box-shadow 0.3s, transform 0.2s ease-in-out;
  border: 2px solid transparent;
  cursor: pointer;
  
  /* Center the text */
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.add-education-btn:hover {
  background: linear-gradient(135deg, #0089b3, #043766);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  transform: translateY(-3px);
}

.add-education-btn:active {
  transform: translateY(1px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.add-education-btn:focus {
  outline: none;
  border: 2px solid #3700b3;
}



/* Form Dialog */
.card {
  background: #fff;
  border-radius: 15px;
  box-shadow: 0 15px 40px rgba(255, 255, 255, 0.1);
}

.stylish-card .v-card-title {
  background: linear-gradient(135deg, #ea0000, #500404);
  color: white;
  padding: 20px;
  font-weight: 600;
}

.form {
  margin-top: 10px;
  background: #f7f7f7;
  padding: 25px;
  border-radius: 15px;
}

.input-field {
  /* margin-bottom: 20px; */
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(231, 183, 179, 0.778);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.input-field:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 15px rgba(109, 191, 46, 0.77);
}

.v-text-field input {
  font-size: 1rem;
  font-weight: 500;
}

.v-select .v-select__selections {
  font-size: 1rem;
  font-weight: 500;
}

.v-form .v-input__control {
  border-radius: 8px;
}

.v-text-field.v-text-field--outlined {
  background-color: #ffffff;
  border-radius: 12px;
}

.v-text-field .v-input__append-inner {
  padding-right: 15px;
}

.v-text-field .v-input__prepend-inner {
  padding-left: 15px;
}

.v-select {
  /* margin-top: 15px; */
}

.v-select .v-select__selections {
  font-size: 1rem;
  font-weight: 500;
}

/* Buttons */
.save-btn {
  background-color: #6200ea;
  color: white;
  border-radius: 50px;
  padding: 12px 30px;
  transition: background-color 0.3s, box-shadow 0.3s;
}

.save-btn:hover {
  background-color: #3700b3;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.cancel-btn {
  color: #6200ea;
  border-radius: 50px;
  padding: 12px 25px;
  transition: color 0.3s ease, text-decoration 0.3s ease;
}

.cancel-btn:hover {
  color: #3700b3;
  text-decoration: underline;
}

/* Card Elevation */
.v-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 25px 60px rgb(255, 255, 255);
}


.v-card-title {
  background: #fff;
  color: #333;
  padding: 20px 30px;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
}

.header-title {
  font-size: 2rem; /* Default font size for larger screens */
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: capitalize;
  color: #ffffff;
  position: relative;
  transition: all 0.3s ease;
}

.header-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 50%;
  height: 3px;
  background-color: #ffffff;
  transform: translateX(-50%);
  opacity: 0;
  transition: all 0.3s ease;
}

.v-card-title:hover .header-title {
  color: #f9f9f9;
}

.v-card-title:hover .header-title::after {
  opacity: 1;
  width: 100%;
}

.v-card-title {
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.v-card-title:hover {
  box-shadow: 0 6px 20px rgb(255, 255, 255);
  transform: translateY(-3px);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-title {
    font-size: 1.8rem; /* Slightly smaller font for medium screens (tablets) */
  }
}

@media (max-width: 600px) {
  .header-title {
    font-size: 1.5rem; /* Smaller font for smaller screens (mobile phones) */
    text-align: center; /* Center text on mobile */
  }

  .v-card-title {
    padding: 15px 20px; /* Reduce padding on mobile */
  }
}


/* Define styles for large and small devices */
.btn-large {
  font-size: 18px;
  padding: 12px 24px;
}

.btn-small {
  font-size: 14px;
  padding: 8px 16px;
}

.icon-large {
  font-size: 24px;
}

.icon-small {
  font-size: 18px;
}

.btn-toggle-large {
  gap: 20px;
}

.btn-toggle-small {
  gap: 10px;
}
</style>